import { useCallback, useEffect } from "react";
import { Switch, Typography, useTheme } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridValueGetter,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  DataGridCard,
  PrefPersistedDataGrid,
  useAppDialog,
  useToolbarHeight,
} from "@pepsico/layout";
import { noop, sortBy } from "es-toolkit";

import type { UserRead, UserUpdate } from "api/model";
import { useGetAllUsers, usePatchUser } from "api/users/users";
import { APP_TITLE } from "defs";
import { booleanValueGetter, dateTimeFormatter } from "utils";

const nameValueGetter: GridValueGetter<
  UserRead,
  string,
  string,
  string | undefined
> = (val) => val || "No Name";

const columns: GridColDef<UserRead>[] = [
  {
    field: "name",
    headerName: "Name",
    valueGetter: nameValueGetter,
    width: 200,
  },
  { field: "email", headerName: "Email Address", width: 250 },
  {
    field: "is_active",
    headerName: "Active Status",
    valueGetter: booleanValueGetter,
    width: 120,
  },
  {
    field: "is_verified",
    headerName: "Verified Status",
    valueGetter: booleanValueGetter,
    width: 120,
  },
  {
    field: "is_superuser",
    headerName: "Admin Status",
    valueGetter: booleanValueGetter,
    width: 120,
  },
  {
    field: "last_login_at",
    headerName: "Last Login",
    valueGetter: dateTimeFormatter,
    width: 250,
  },
];

const defaultUsers: UserRead[] = [];

const Users: React.FC = () => {
  const theme = useTheme();
  const toolbarHeight = useToolbarHeight();
  const apiRef = useGridApiRef();
  const { data: users = defaultUsers, refetch } = useGetAllUsers({
    query: {
      select: (res) => res.data,
    },
  });

  const { setAppDialog } = useAppDialog();

  const updateUserMutation = usePatchUser({
    mutation: {
      onError: (error) => {
        const message =
          error instanceof Error ? error.message : "An error occurred";

        setAppDialog({
          confirmText: "Close",
          message: <>{message}</>,
          onConfirm: noop,
          open: true,
          title: "Error",
        });
      },
      onSuccess: () => {
        void refetch();
      },
    },
  });

  const handleStatusChange = useCallback(
    (userId: number, field: keyof UserUpdate, value: boolean) => {
      updateUserMutation.mutate({
        data: { [field]: value },
        userId,
      });
    },
    [updateUserMutation],
  );

  useEffect(() => {
    const cols = apiRef.current!.getAllColumns();
    const isActiveColumn = cols.find((col) => col.field === "is_active");
    const isVerifiedColumn = cols.find((col) => col.field === "is_verified");
    const isSuperuserColumn = cols.find((col) => col.field === "is_superuser");

    if (isActiveColumn) {
      isActiveColumn.renderCell = (
        params: GridRenderCellParams<UserRead, boolean>,
      ) => (
        <Switch
          checked={params.value}
          disabled={updateUserMutation.isPending}
          onChange={(_, checked) => {
            handleStatusChange(params.row.id, "is_active", checked);
          }}
          size="small"
        />
      );
    }

    if (isVerifiedColumn) {
      isVerifiedColumn.renderCell = (
        params: GridRenderCellParams<UserRead, boolean>,
      ) => (
        <Switch
          checked={params.value}
          disabled={updateUserMutation.isPending}
          onChange={(_, checked) => {
            handleStatusChange(params.row.id, "is_verified", checked);
          }}
          size="small"
        />
      );
    }

    if (isSuperuserColumn) {
      isSuperuserColumn.renderCell = (
        params: GridRenderCellParams<UserRead, boolean>,
      ) => (
        <Switch
          checked={params.value}
          disabled={updateUserMutation.isPending}
          onChange={(_, checked) => {
            handleStatusChange(params.row.id, "is_superuser", checked);
          }}
          size="small"
        />
      );
    }

    apiRef.current!.updateColumns(cols);
  }, [apiRef, handleStatusChange, updateUserMutation.isPending]);

  return (
    <>
      <title>{`${APP_TITLE} - User Administration`}</title>
      <Typography gutterBottom sx={{ fontWeight: "bold" }} variant="h6">
        User Administration
      </Typography>
      <DataGridCard>
        <PrefPersistedDataGrid
          apiRef={apiRef}
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row: UserRead) => row.id}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          pagination
          prefKey="users-table"
          rows={sortBy(users, ["name", "email"])}
          sx={{
            maxHeight: `calc(100vh - ${toolbarHeight}px - ${theme.spacing(10)})`,
          }}
        />
      </DataGridCard>
    </>
  );
};

export default Users;
